import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, defer, race } from 'rxjs';
import {
  take,
  timeout,
  withLatestFrom,
  exhaustMap,
  switchMap,
  mergeMap,
  concatMap,
  map,
  filter,
  share,
  catchError, 
} from 'rxjs/operators'; 
import { Store } from '@ngrx/store';
import { BackendService } from '@toro/common/shared/services/backend/backend.service';
import { RoutesTable } from '@toro/common/shared/resources/routes-table.model';
import { DefaultResponse } from '@toro/common/shared/models/form/DefaultResponse';
import { StocksExchangeCheckoutAction } from './stocks-exchange-checkout.action';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { splitOrderByLot } from './stocks-exchange-checkout.utils';
import { StocksMarketAction } from '@toro/common/shared/state-management/stocks-market/stocks-market.action';

const INSTRUMENT_TIMEOUT_MS = 5000;

@Injectable()
export class StocksExchangeCheckoutEffects {
  constructor(
    private actions$: Actions<StocksExchangeCheckoutAction>,
    private backendService: BackendService,
    private store$: Store<AppState>,
  ) {}

  /**
   * Criar uma nova ordem de cliente
   */
  newOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.newOrder),
      withLatestFrom(this.store$.select((s) => s.authentication)),
      filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
      exhaustMap(([action, auth]) => {
        const sendOrder = (order) =>
          this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.newOrder.uri, order, action.payload.token);

        return this.resolveStandardLot(action.payload.data?.symbol)
          .pipe(
            concatMap((standardLot) => {
              const [firstOrder, ...remainingOrders] = splitOrderByLot(action.payload.data, standardLot);
              return remainingOrders.reduce(
                (chain$, order) => chain$.pipe(
                  concatMap((previous) => (previous.ok ? sendOrder(order).pipe(map((result) => (result.ok ? previous : result))) : of(previous)))
                ),
                sendOrder(firstOrder)
              );
            }),
            map((result) => {
              if (result.ok) {
                return { type: StocksExchangeCheckoutAction.newOrderSuccess, payload: result.value };
              }
              return { type: StocksExchangeCheckoutAction.newOrderError, payload: result.value };
            }),
            catchError((error) => { 
              return of({ type: StocksExchangeCheckoutAction.newOrderError, payload: error });
            })
          );
      }),
      share()
    )
  );

  /**
   * Obtém o lote padrão do ativo fracionário, buscando o instrumento caso ainda não esteja no store
   */
  private resolveStandardLot(symbol: string): Observable<number> {
    const normalizedSymbol = (symbol || '').toUpperCase();
    if (!normalizedSymbol.endsWith('F')) return of(undefined);

    const instrument$ = this.store$.select((s) => s.stocksExchange.stocksMarket.instruments).pipe(
      map((instruments) => instruments?.[normalizedSymbol]),
      filter((instrument) => !!instrument),
      map((instrument) => instrument.standardLot)
    );
    const error$ = this.actions$.pipe(ofType(StocksMarketAction.getInstrumentInfoError), map(() => undefined));

    return defer(() => {
      this.store$.dispatch({ type: StocksMarketAction.getInstrumentInfo, payload: normalizedSymbol });
      return race(instrument$, error$);
    }).pipe(
      take(1),
      timeout(INSTRUMENT_TIMEOUT_MS),
      catchError(() => of(undefined))
    );
  }

  /**
   * Editar uma ordem de cliente
   */
  editOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.editOrder),
      withLatestFrom(this.store$.select((s) => s.authentication)),
      filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
      switchMap(([action, auth]) => {
        return this.backendService.post<DefaultResponse>(RoutesTable.exchange.routes.editOrder.uri, action.payload.data, action.payload.token)
          .pipe(
            map((result) => {
              if (result.ok) {
                return { type: StocksExchangeCheckoutAction.editOrderSuccess, payload: result.value };
              }
              return { type: StocksExchangeCheckoutAction.editOrderError, payload: result.value };
            }),
            catchError((error) => { 
              return of({ type: StocksExchangeCheckoutAction.editOrderError, payload: error });
            })
          );
      }),
      share()
    )
  );

  /**
   * Criar uma nova estratégia de cliente
   */
  newStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.newStrategy),
      withLatestFrom(this.store$.select((s) => s.authentication)),
      filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
      switchMap(([action, auth]) => {
        return this.backendService.post<DefaultResponse>(
          RoutesTable.exchange.routes.newStrategy.uri,
          action.payload.data,
          action.payload.token
        ).pipe(
          map((result) => {
            if (result.ok) {
              return { type: StocksExchangeCheckoutAction.newStrategySuccess, payload: result.value };
            }
            return { type: StocksExchangeCheckoutAction.newStrategyError, payload: result.value };
          }),
          catchError((error) => { 
            return of({ type: StocksExchangeCheckoutAction.newStrategyError, payload: error });
          })
        );
      }),
      share()
    )
  );

  /**
   * Editar uma nova estratégia de cliente
   */
  editStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.editStrategy),
      withLatestFrom(this.store$.select((s) => s.authentication)),
      filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
      switchMap(([action, auth]) => {
        return this.backendService.post<DefaultResponse>(
          RoutesTable.exchange.routes.editStrategy.uri,
          action.payload.data,
          action.payload.token
        ).pipe(
          map((result) => {
            if (result.ok) {
              return { type: StocksExchangeCheckoutAction.editStrategySuccess, payload: result.value };
            }
            return { type: StocksExchangeCheckoutAction.editStrategyError, payload: result.value };
          }),
          catchError((error) => { 
            return of({ type: StocksExchangeCheckoutAction.editStrategyError, payload: error });
          })
        );
      }),
      share()
    )
  );

  /**
   * Encerrar uma estratégia de cliente
   */
  shutDownStrategy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.shutDownStrategy),
      withLatestFrom(this.store$.select((s) => s.authentication)),
      filter(([action, auth]) => auth.currentUser && auth.currentUser.token),
      switchMap(([action, auth]) => {
        return this.backendService.post<DefaultResponse>(
          RoutesTable.exchange.routes.shutDownStrategy.uri,
          action.payload, 
          action.payload.token
        ).pipe(
          map((result) => {
            if (result.ok) {
              return { type: StocksExchangeCheckoutAction.shutDownStrategySuccess, payload: result.value };
            }
            return { type: StocksExchangeCheckoutAction.shutDownStrategyError, payload: result.value };
          }),
          catchError((error) => { 
            return of({ type: StocksExchangeCheckoutAction.shutDownStrategyError, payload: error });
          })
        );
      }),
      share()
    )
  );

  /**
   * Obtém os próximos dias úteis
   */
  nextWorkingDay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StocksExchangeCheckoutAction.nextWorkingDays),
      switchMap((action) => {
        const currentDate = new Date();
        const currentDateString = currentDate
          .toISOString()
          .split('T')[0]
          .split('-');

        const endDate = new Date();
        endDate.setFullYear((+currentDate.getFullYear()) + 1);
        const endDateString = endDate
          .toISOString()
          .split('T')[0]
          .split('-');

        const today = `${currentDateString[0]}/${currentDateString[1]}/${currentDateString[2]}`;
        const end = `${endDateString[0]}/${endDateString[1]}/${endDateString[2]}`;

        const url = RoutesTable.composeUri(RoutesTable.corporation.routes.getWorkingDaysBovespa, today, end);

        return this.backendService.get<DefaultResponse>(url)
          .pipe(
            map((result) => {
              if (result.ok) {
                return { type: StocksExchangeCheckoutAction.nextWorkingDaysSuccess, payload: result.value };
              }
              return { type: StocksExchangeCheckoutAction.nextWorkingDaysError, payload: result.value };
            }),
            catchError((error) => {
              return of({ type: StocksExchangeCheckoutAction.nextWorkingDaysError, payload: error });
            })
          );
      }),
      share()
    )
  );
}